import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Brain,
  CalendarDays,
  Check,
  Copy,
  Flag,
  Gamepad2,
  Palette,
  Play,
  RotateCcw,
  Sparkles
} from "lucide-react";
import { QUESTIONS } from "./data/questions";
import "./styles.css";

const ROUND_COUNT = 5;

const MODES = [
  {
    id: "classic-toon",
    label: "Classic Toon",
    icon: Sparkles,
    description: "Original cartoon color memory challenges."
  },
  {
    id: "logo-memory",
    label: "Logo Memory",
    icon: Gamepad2,
    description: "Brand-style color challenge with abstract marks."
  },
  {
    id: "flag-color",
    label: "Flag Color",
    icon: Flag,
    description: "Flag-inspired crosses, stripes, suns, and fields."
  },
  {
    id: "daily",
    label: "Daily",
    icon: CalendarDays,
    description: "The same five toon tone questions for everyone today."
  }
];

const PAGE_META = {
  "/": {
    title: "Toon Tone - Free Cartoon Color Guessing Game",
    description:
      "Play Toon Tone Game, a free color guessing game and cartoon color guessing game with toon, logo, flag, and palette modes.",
    h1: "Toon Tone Game",
    intro:
      "Toon Tone Game is a free color guessing game where you tune hue, saturation, and brightness to match toon-inspired colors. Play toontone-style rounds with original art, quick feedback, and no sign-up."
  },
  "/toon-tone-game": {
    title: "Toon Tone Game - Play Free Color Guessing Game Online",
    description:
      "Play Toon Tone Game online for free. Guess iconic toon, logo, flag, and palette colors using HSB sliders.",
    h1: "Toon Tone Game Online",
    intro:
      "This toon tone game turns color memory into a fast browser challenge. Match the target color, compare HSB differences, and push your score toward 500."
  },
  "/toon-tone-cartoon": {
    title: "Toon Tone Cartoon - Guess Cartoon Colors Online",
    description:
      "Play Toon Tone Cartoon mode with original cartoon characters and color memory prompts.",
    h1: "Toon Tone Cartoon",
    intro:
      "Toon Tone Cartoon focuses on original character details like scarves, hats, capes, and masks. No real cartoon IP is used, just playful toon color memory."
  },
  "/toon-tones": {
    title: "Toon Tones - Practice Toon Color Palettes",
    description:
      "Practice toon tones and color palettes with HSB sliders in a free browser color game.",
    h1: "Toon Tones Palette Practice",
    intro:
      "Practice toon tones with bright palette prompts, learn how hue, saturation, and brightness affect memory, and build a sharper eye for cartoon color palettes."
  },
  "/toon-tone-vercel": {
    title: "Toon Tone Vercel Alternative - Play Online Free",
    description:
      "Looking for Toon Tone Vercel? Play this free online Toon Tone alternative in your browser.",
    h1: "Toon Tone Vercel Alternative",
    intro:
      "If you searched for toon tone vercel, this free browser version gives you a quick Toon Tone alternative with daily challenges, original artwork, and instant play."
  }
};

function clampPath(path) {
  return PAGE_META[path] ? path : "/";
}

function hsvToRgb({ h, s, b }) {
  const sat = s / 100;
  const val = b / 100;
  const chroma = val * sat;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = val - chroma;
  let r = 0;
  let g = 0;
  let blue = 0;

  if (h < 60) [r, g, blue] = [chroma, x, 0];
  else if (h < 120) [r, g, blue] = [x, chroma, 0];
  else if (h < 180) [r, g, blue] = [0, chroma, x];
  else if (h < 240) [r, g, blue] = [0, x, chroma];
  else if (h < 300) [r, g, blue] = [x, 0, chroma];
  else [r, g, blue] = [chroma, 0, x];

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((blue + m) * 255)
  };
}

function hsvToHex(hsv) {
  const { r, g, b } = hsvToRgb(hsv);
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function scoreGuess(guess, target) {
  const hueRaw = Math.abs(guess.h - target.h);
  const hueDiff = Math.min(hueRaw, 360 - hueRaw) / 180;
  const satDiff = Math.abs(guess.s - target.s) / 100;
  const briDiff = Math.abs(guess.b - target.b) / 100;
  const weightedDiff = hueDiff * 0.5 + satDiff * 0.25 + briDiff * 0.25;
  return {
    hueDiff,
    satDiff,
    briDiff,
    roundScore: Math.round(Math.max(0, 100 - weightedDiff * 100))
  };
}

function seededRandom(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += h << 13;
    h ^= h >>> 7;
    h += h << 3;
    h ^= h >>> 17;
    h += h << 5;
    return ((h >>> 0) % 100000) / 100000;
  };
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function shuffleItems(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

function modePool(mode) {
  return QUESTIONS.filter((question) => {
    if (mode === "classic-toon") return question.mode === "classic-toon";
    if (mode === "logo-memory") return question.mode === "logo-memory";
    if (mode === "flag-color") return question.mode === "flag-color";
    if (mode === "palette") return question.mode === "palette";
    return true;
  });
}

function pickQuestions(mode, previousIds = []) {
  if (mode === "daily") {
    const rng = seededRandom(todayKey());
    return [...QUESTIONS]
      .map((question) => ({ question, sort: rng() }))
      .sort((a, b) => a.sort - b.sort)
      .slice(0, ROUND_COUNT)
      .map((entry) => entry.question);
  }

  const pool = modePool(mode);
  const progression = ["easy", "easy", "medium", "medium", "hard"];
  const picked = [];
  const previous = new Set(previousIds);

  progression.forEach((difficulty) => {
    const alreadyPicked = new Set(picked.map((question) => question.id));
    const freshSameDifficulty = pool.filter(
      (question) =>
        question.difficulty === difficulty &&
        !alreadyPicked.has(question.id) &&
        !previous.has(question.id)
    );
    const sameDifficulty = pool.filter(
      (question) => question.difficulty === difficulty && !alreadyPicked.has(question.id)
    );
    const anyFresh = pool.filter(
      (question) => !alreadyPicked.has(question.id) && !previous.has(question.id)
    );
    const anyAvailable = pool.filter((question) => !alreadyPicked.has(question.id));
    const candidates =
      freshSameDifficulty.length > 0
        ? freshSameDifficulty
        : sameDifficulty.length > 0
          ? sameDifficulty
          : anyFresh.length > 0
            ? anyFresh
            : anyAvailable;

    picked.push(shuffleItems(candidates)[0]);
  });

  return picked.filter(Boolean);
}

function resultLabel(score) {
  if (score >= 450) return "Elite color memory.";
  if (score >= 380) return "Great eye for toon colors.";
  if (score >= 300) return "Solid color instinct.";
  return "Keep training your color memory.";
}

function analyzeGuesses(guesses) {
  if (!guesses.length) return [];
  const totalScore = guesses.reduce((sum, guess) => sum + guess.roundScore, 0);
  const avgHueDiff =
    guesses.reduce((sum, guess) => sum + guess.hueDiff, 0) / guesses.length;
  const avgSatDrift =
    guesses.reduce((sum, guess) => sum + (guess.selected.s - guess.target.s), 0) /
    guesses.length;
  const avgBriDrift =
    guesses.reduce((sum, guess) => sum + (guess.selected.b - guess.target.b), 0) /
    guesses.length;
  const notes = [];

  if (avgHueDiff < 0.12) notes.push("Your hue memory is excellent.");
  if (avgSatDrift > 10) notes.push("You tend to choose more saturated colors.");
  if (avgSatDrift < -10) notes.push("You tend to choose softer colors.");
  if (avgBriDrift > 10) notes.push("You tend to choose brighter colors.");
  if (avgBriDrift < -10) notes.push("You tend to choose darker colors.");
  notes.push(resultLabel(totalScore));
  return notes;
}

function App() {
  const [path, setPath] = useState(clampPath(window.location.pathname));
  const page = PAGE_META[path];
  const preferredMode = path === "/toon-tone-cartoon" ? "classic-toon" : path === "/toon-tones" ? "palette" : "classic-toon";
  const [mode, setMode] = useState(preferredMode);

  useEffect(() => {
    document.title = page.title;
    document.querySelector("meta[name='description']")?.setAttribute("content", page.description);
    document.querySelector("link[rel='canonical']")?.setAttribute("href", `${window.location.origin}${path}`);
  }, [page, path]);

  useEffect(() => {
    const handlePop = () => {
      const next = clampPath(window.location.pathname);
      setPath(next);
      if (next === "/toon-tone-cartoon") setMode("classic-toon");
      if (next === "/toon-tones") setMode("palette");
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const navigate = (nextPath) => {
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
    if (nextPath === "/toon-tone-cartoon") setMode("classic-toon");
    if (nextPath === "/toon-tones") setMode("palette");
  };

  return (
    <main>
      <Header path={path} navigate={navigate} />
      <section className="hero-shell">
        <div className="hero-copy">
          <span className="eyebrow">Free browser HSB color game</span>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
        </div>
        <GameBoard mode={mode} setMode={setMode} />
      </section>
      <SeoContent path={path} navigate={navigate} />
    </main>
  );
}

function Header({ path, navigate }) {
  const links = [
    ["/", "Home"],
    ["/toon-tone-game", "Game"],
    ["/toon-tone-cartoon", "Cartoon"],
    ["/toon-tones", "Toon Tones"],
    ["/toon-tone-vercel", "Vercel Alt"]
  ];

  return (
    <header className="topbar">
      <button className="brand" type="button" onClick={() => navigate("/")}>
        <span className="brand-mark">TT</span>
        <span>Toon Tone Game</span>
      </button>
      <nav aria-label="Primary pages">
        {links.map(([href, label]) => (
          <button
            key={href}
            className={path === href ? "nav-link active" : "nav-link"}
            type="button"
            onClick={() => navigate(href)}
          >
            {label}
          </button>
        ))}
      </nav>
    </header>
  );
}

function GameBoard({ mode, setMode }) {
  const [rounds, setRounds] = useState(() => pickQuestions(mode));
  const [roundIndex, setRoundIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState({ h: 210, s: 72, b: 82 });
  const [guesses, setGuesses] = useState([]);
  const [status, setStatus] = useState("playing");
  const question = rounds[roundIndex];
  const lastGuess = guesses[guesses.length - 1];
  const score = guesses.reduce((sum, guess) => sum + guess.roundScore, 0);

  useEffect(() => {
    const newRounds = pickQuestions(mode);
    setRounds(newRounds);
    setRoundIndex(0);
    setGuesses([]);
    setStatus("playing");
    setSelectedColor({ h: 210, s: 72, b: 82 });
  }, [mode]);

  useEffect(() => {
    const games = Number(localStorage.getItem("totalGamesPlayed") || "0");
    if (status === "finished" && guesses.length === ROUND_COUNT) {
      const bestKey = mode === "daily" ? "dailyBestScore" : "bestScore";
      const previousBest = Number(localStorage.getItem(bestKey) || "0");
      localStorage.setItem(bestKey, String(Math.max(previousBest, score)));
      localStorage.setItem("totalGamesPlayed", String(games + 1));
      if (mode === "daily") localStorage.setItem("lastPlayedDate", todayKey());
    }
  }, [status, guesses.length, mode, score]);

  const restart = () => {
    const previousIds = rounds.map((round) => round.id);
    setRounds(pickQuestions(mode, previousIds));
    setRoundIndex(0);
    setGuesses([]);
    setStatus("playing");
    setSelectedColor({ h: 210, s: 72, b: 82 });
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
  };

  const submitGuess = () => {
    const result = scoreGuess(selectedColor, question.target);
    setGuesses([
      ...guesses,
      {
        questionId: question.id,
        title: question.title,
        selected: { ...selectedColor },
        target: question.target,
        ...result
      }
    ]);
    setStatus("revealed");
  };

  const nextRound = () => {
    if (roundIndex + 1 >= ROUND_COUNT) {
      setStatus("finished");
      return;
    }
    setRoundIndex(roundIndex + 1);
    setSelectedColor({ h: 210, s: 72, b: 82 });
    setStatus("playing");
  };

  return (
    <section className="game-card" aria-label="Playable Toon Tone Game">
      <ModeTabs mode={mode} onChange={changeMode} />
      {status === "finished" ? (
        <FinalResult mode={mode} guesses={guesses} onRestart={restart} />
      ) : (
        <>
          <div className="game-meta">
            <ScoreBadge score={score} roundIndex={roundIndex} />
            <span>{mode === "daily" ? `Daily seed ${todayKey()}` : MODES.find((item) => item.id === mode)?.description}</span>
          </div>
          <ActionButtons
            status={status}
            roundIndex={roundIndex}
            onSubmit={submitGuess}
            onNext={nextRound}
            onRestart={restart}
            className="top-actions"
          />
          <div className="play-grid">
            <QuestionCard
              question={question}
              selectedColor={selectedColor}
              revealed={status === "revealed"}
            />
            <ColorControls
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
              locked={status === "revealed"}
              target={question.target}
              revealed={status === "revealed"}
            />
          </div>
          {status === "revealed" && <RoundResult guess={lastGuess} />}
          <ActionButtons
            status={status}
            roundIndex={roundIndex}
            onSubmit={submitGuess}
            onNext={nextRound}
            onRestart={restart}
            className="bottom-actions"
          />
        </>
      )}
    </section>
  );
}

function ActionButtons({ status, roundIndex, onSubmit, onNext, onRestart, className = "" }) {
  return (
    <div className={`game-actions ${className}`}>
      {status === "playing" ? (
        <button className="primary-action" type="button" onClick={onSubmit}>
          <Check size={20} />
          Submit Guess
        </button>
      ) : (
        <button className="primary-action" type="button" onClick={onNext}>
          <Play size={20} />
          {roundIndex + 1 >= ROUND_COUNT ? "See Final Result" : "Next Round"}
        </button>
      )}
      <button className="ghost-action" type="button" onClick={onRestart}>
        <RotateCcw size={18} />
        Play Again
      </button>
    </div>
  );
}

function ModeTabs({ mode, onChange }) {
  return (
    <div className="mode-tabs" role="tablist" aria-label="Game modes">
      {MODES.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            className={mode === item.id ? "mode-tab active" : "mode-tab"}
            type="button"
            onClick={() => onChange(item.id)}
          >
            <Icon size={18} />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function ScoreBadge({ score, roundIndex }) {
  return (
    <div className="score-badge">
      <Brain size={19} />
      <strong>{score}/500</strong>
      <span>Round {roundIndex + 1}/5</span>
    </div>
  );
}

function QuestionCard({ question, selectedColor, revealed }) {
  return (
    <article className="question-card">
      <div>
        <span className="mini-label">{question.title}</span>
        <h2>{question.prompt}</h2>
      </div>
      <TargetArt question={question} />
      <p>
        Use the artwork as your target, then recreate that highlighted color with
        HSB sliders. The exact target values stay hidden until you submit.
      </p>
    </article>
  );
}

function ColorControls({ selectedColor, setSelectedColor, locked, target, revealed }) {
  const selectedHex = hsvToHex(selectedColor);
  const targetHex = hsvToHex(target);

  const updateValue = (key, value) => {
    setSelectedColor({ ...selectedColor, [key]: Number(value) });
  };

  return (
    <aside className="controls-card" style={{ "--choice": selectedHex }}>
      <div className="controls-summary">
        <div>
          <span>Your choice</span>
          <strong>H {selectedColor.h} S {selectedColor.s} B {selectedColor.b}</strong>
        </div>
        <div className="controls-pill">{revealed ? "Target shown" : "Tune"}</div>
      </div>
      <div className="slider-panel">
        <div className="preview-row">
          <ColorPreview label="Your color" color={selectedHex} hsv={selectedColor} />
          <ColorPreview
            label="Target"
            color={targetHex}
            hsv={revealed ? target : null}
            hiddenText="HSB values hidden until submit"
          />
        </div>
        <Slider
          label="Hue"
          min={0}
          max={360}
          value={selectedColor.h}
          suffix="°"
          disabled={locked}
          onChange={(value) => updateValue("h", value)}
          track="hue"
          selectedColor={selectedHex}
        />
        <Slider
          label="Saturation"
          min={0}
          max={100}
          value={selectedColor.s}
          suffix="%"
          disabled={locked}
          onChange={(value) => updateValue("s", value)}
          track="saturation"
          selectedColor={selectedHex}
        />
        <Slider
          label="Brightness"
          min={0}
          max={100}
          value={selectedColor.b}
          suffix="%"
          disabled={locked}
          onChange={(value) => updateValue("b", value)}
          track="brightness"
          selectedColor={selectedHex}
        />
        <div className="hex-readout">{selectedHex.toUpperCase()}</div>
      </div>
    </aside>
  );
}

function ColorPreview({ label, color, hsv, hiddenText = "Hidden until submit" }) {
  return (
    <div className="color-preview">
      <span>{label}</span>
      <div style={{ background: color }} />
      <small>{hsv ? `H ${hsv.h} · S ${hsv.s} · B ${hsv.b}` : hiddenText}</small>
    </div>
  );
}

function Slider({ label, min, max, value, onChange, disabled, track, suffix = "", selectedColor }) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <label className="slider-row" style={{ "--value-percent": `${percent}%`, "--selected-color": selectedColor }}>
      <span>
        {label}
        <strong>{value}{suffix}</strong>
      </span>
      <input
        className={`slider ${track}`}
        type="range"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function RoundResult({ guess }) {
  return (
    <div className="round-result">
      <strong>Round score: {guess.roundScore}/100</strong>
      <span>Hue diff {Math.round(guess.hueDiff * 180)}°</span>
      <span>Saturation diff {Math.round(guess.satDiff * 100)}</span>
      <span>Brightness diff {Math.round(guess.briDiff * 100)}</span>
    </div>
  );
}

function FinalResult({ mode, guesses, onRestart }) {
  const total = guesses.reduce((sum, guess) => sum + guess.roundScore, 0);
  const average = Math.round((total / 500) * 100);
  const notes = analyzeGuesses(guesses);
  const [copied, setCopied] = useState(false);
  const bestKey = mode === "daily" ? "dailyBestScore" : "bestScore";
  const best = Number(localStorage.getItem(bestKey) || "0");

  const copyText =
    mode === "daily"
      ? `Toon Tone Daily Challenge\nScore: ${total}/500\nResult: ${resultLabel(total)}\nCan you beat me?`
      : `I scored ${total}/500 on Toon Tone Game.\nMy color memory: ${resultLabel(total)}\nPlay: ${window.location.origin}/toon-tone-game`;

  const copyResult = async () => {
    await navigator.clipboard.writeText(copyText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <section className="final-result">
      <span className="mini-label">Final Result</span>
      <h2>{total}/500</h2>
      <p>Average accuracy: {average}%</p>
      <p>Best saved score: {Math.max(best, total)}/500</p>
      <div className="analysis-list">
        {notes.map((note) => (
          <span key={note}>{note}</span>
        ))}
      </div>
      <div className="history-list">
        {guesses.map((guess, index) => (
          <div key={guess.questionId}>
            <span>{index + 1}. {guess.title}</span>
            <strong>{guess.roundScore}</strong>
          </div>
        ))}
      </div>
      <div className="game-actions">
        <button className="primary-action" type="button" onClick={onRestart}>
          <RotateCcw size={20} />
          Play Again
        </button>
        <button className="ghost-action" type="button" onClick={copyResult}>
          <Copy size={18} />
          {copied ? "Copied" : "Copy Result"}
        </button>
      </div>
    </section>
  );
}

function TargetArt({ question }) {
  const c = hsvToHex(question.target);

  if (question.mode === "logo-memory") {
    return <LogoArt shape={question.shape} color={c} />;
  }
  if (question.mode === "flag-color") {
    return <FlagArt shape={question.shape} color={c} />;
  }
  if (question.mode === "palette") {
    return <PaletteArt shape={question.shape} color={c} />;
  }

  return <CharacterArt question={question} color={c} />;
}

function CharacterArt({ question, color }) {
  const ink = "#263238";
  const part = (name, fallback) => (question.targetPart === name ? color : fallback);
  const shape = question.shape;
  const base = {
    fox: ["#ff8a25", "#ffd28f"],
    robot: ["#c8d8e8", "#eef6ff"],
    wizard: ["#f4c184", "#ffe0a8"],
    cat: ["#d7ad82", "#ffe2b7"],
    dino: ["#7acb6a", "#d8f1a2"],
    bunny: ["#f4e4d8", "#fff4e9"],
    penguin: ["#38475c", "#fff2d0"],
    moth: ["#c9e8a0", "#fff3b0"],
    panda: ["#f7f4eb", "#ffffff"],
    cloud: ["#f5fbff", "#ffffff"],
    sprout: ["#91cf62", "#dff4a2"],
    octo: ["#f098bc", "#ffd2e1"],
    star: ["#ffcf4d", "#fff1a7"],
    turtle: ["#77bd74", "#d6eda4"],
    mushroom: ["#f0d8b8", "#fff0cf"]
  }[shape] || ["#ffc978", "#ffe3b0"];
  const shell = part("shell", "#4d9f62");
  const hat = part("hat", part("cap", "#6e55d9"));
  const scarf = part("scarf", part("headband", part("ribbon", "#4fb0ef")));
  const belly = part("belly", part("mask", "#fff1c8"));
  const glasses = part("glasses", "#ffb23f");
  const boots = part("boots", "#263238");
  const wing = part("wings", part("cape", "#9be3ff"));
  const spots = part("spots", "#f2554a");
  const antenna = part("antenna", "#5fb7ff");
  const bow = part("bow", "#ff6683");

  return (
    <svg className="target-art" viewBox="0 0 360 260" role="img" aria-label={question.title}>
      <defs>
        <filter id={`softShadow-${question.id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor="#263238" floodOpacity="0.18" />
        </filter>
      </defs>
      <rect x="18" y="16" width="324" height="228" rx="32" fill="#fff6d8" />
      <circle cx="180" cy="127" r="88" fill="#ffe0a0" opacity="0.9" />
      <ellipse cx="180" cy="218" rx="92" ry="16" fill="#d8cfb9" opacity="0.75" />

      {(shape === "moth" || shape === "cloud") && (
        <>
          <path d="M117 128 C70 92 66 50 107 43 C141 37 157 81 151 126 Z" fill={wing} stroke={ink} strokeWidth="7" />
          <path d="M243 128 C290 92 294 50 253 43 C219 37 203 81 209 126 Z" fill={wing} stroke={ink} strokeWidth="7" />
        </>
      )}

      {shape === "star" && (
        <path d="M180 37 L201 91 L259 94 L213 130 L228 187 L180 155 L132 187 L147 130 L101 94 L159 91 Z" fill={base[0]} stroke={ink} strokeWidth="7" filter={`url(#softShadow-${question.id})`} />
      )}

      {shape !== "star" && (
        <g filter={`url(#softShadow-${question.id})`}>
          {(shape === "fox" || shape === "cat" || shape === "bunny") && (
            <>
              <path d="M120 78 L90 31 L143 58 Z" fill={base[0]} stroke={ink} strokeWidth="7" />
              <path d="M240 78 L270 31 L217 58 Z" fill={base[0]} stroke={ink} strokeWidth="7" />
              <path d="M112 70 L96 45 L126 61 Z" fill="#fff0c7" />
              <path d="M248 70 L264 45 L234 61 Z" fill="#fff0c7" />
            </>
          )}
          {shape === "mushroom" && (
            <>
              <path d="M101 83 C110 35 250 35 259 83 C249 116 112 116 101 83 Z" fill="#f36b54" stroke={ink} strokeWidth="7" />
              <circle cx="141" cy="75" r="14" fill={spots} />
              <circle cx="190" cy="62" r="16" fill={spots} />
              <circle cx="225" cy="83" r="12" fill={spots} />
            </>
          )}
          {shape === "wizard" && <path d="M129 77 L180 13 L231 77 Z" fill={hat} stroke={ink} strokeWidth="7" />}
          {shape === "penguin" && <path d="M130 67 Q180 21 230 67 Z" fill={hat} stroke={ink} strokeWidth="7" />}
          {shape === "robot" && (
            <>
              <path d="M180 43 L180 21" stroke={antenna} strokeWidth="9" strokeLinecap="round" />
              <circle cx="180" cy="18" r="10" fill={antenna} stroke={ink} strokeWidth="5" />
            </>
          )}
          {shape === "sprout" && (
            <path d="M178 55 C150 27 131 35 128 68 C153 70 168 64 178 55 C193 29 218 31 229 61 C205 72 190 68 178 55 Z" fill="#60b94c" stroke={ink} strokeWidth="6" />
          )}
          {shape === "turtle" && (
            <ellipse cx="180" cy="137" rx="76" ry="64" fill={shell} stroke={ink} strokeWidth="7" />
          )}
          {shape === "octo" && (
            <>
              <path d="M115 177 C116 205 143 197 146 176" fill="none" stroke={base[0]} strokeWidth="18" strokeLinecap="round" />
              <path d="M166 180 C163 211 194 211 192 180" fill="none" stroke={base[0]} strokeWidth="18" strokeLinecap="round" />
              <path d="M214 176 C220 199 246 201 245 174" fill="none" stroke={base[0]} strokeWidth="18" strokeLinecap="round" />
            </>
          )}
          <circle cx="180" cy="120" r="73" fill={shape === "turtle" ? base[1] : base[0]} stroke={ink} strokeWidth="7" />
          <ellipse cx="180" cy="145" rx="45" ry="34" fill={belly} stroke={ink} strokeWidth="6" />
          <circle cx="153" cy="112" r="8" fill={ink} />
          <circle cx="207" cy="112" r="8" fill={ink} />
          <path d="M159 143 Q180 157 202 143" fill="none" stroke={ink} strokeWidth="6" strokeLinecap="round" />
          {shape === "panda" && (
            <>
              <circle cx="148" cy="108" r="24" fill="#263238" opacity="0.95" />
              <circle cx="212" cy="108" r="24" fill="#263238" opacity="0.95" />
              <circle cx="148" cy="108" r="7" fill="#fff" />
              <circle cx="212" cy="108" r="7" fill="#fff" />
            </>
          )}
          {shape === "cloud" && (
            <>
              <circle cx="132" cy="118" r="34" fill="#f5fbff" stroke={ink} strokeWidth="6" />
              <circle cx="180" cy="98" r="43" fill="#f5fbff" stroke={ink} strokeWidth="6" />
              <circle cx="226" cy="122" r="35" fill="#f5fbff" stroke={ink} strokeWidth="6" />
            </>
          )}
          <rect x="118" y="173" width="124" height="39" rx="18" fill={scarf} stroke={ink} strokeWidth="7" />
          {(question.targetPart === "glasses" || shape === "panda") && (
            <>
              <circle cx="151" cy="106" r="25" fill="none" stroke={glasses} strokeWidth="7" />
              <circle cx="209" cy="106" r="25" fill="none" stroke={glasses} strokeWidth="7" />
              <path d="M176 106 H184" stroke={glasses} strokeWidth="7" strokeLinecap="round" />
            </>
          )}
          {(shape === "bunny" || question.targetPart === "bow") && (
            <>
              <path d="M154 62 L121 43 L130 83 Z" fill={bow} stroke={ink} strokeWidth="5" />
              <path d="M206 62 L239 43 L230 83 Z" fill={bow} stroke={ink} strokeWidth="5" />
              <circle cx="180" cy="64" r="12" fill={bow} stroke={ink} strokeWidth="5" />
            </>
          )}
          <path d="M127 208 L103 240" stroke={boots} strokeWidth="14" strokeLinecap="round" />
          <path d="M233 208 L257 240" stroke={boots} strokeWidth="14" strokeLinecap="round" />
        </g>
      )}
    </svg>
  );
}

function LogoArt({ shape, color }) {
  return (
    <svg className="target-art" viewBox="0 0 320 230" role="img" aria-label="Brand-style color challenge">
      <rect x="18" y="18" width="284" height="194" rx="28" fill="#f7fbff" />
      <rect x="74" y="50" width="172" height="132" rx="34" fill="#ffffff" stroke="#263238" strokeWidth="6" />
      <circle cx="160" cy="116" r="44" fill={shape === "logo-orbit" ? color : "#ffe3a0"} />
      <path d="M94 138 C122 82 187 82 226 124" fill="none" stroke={shape === "logo-wave" ? color : "#263238"} strokeWidth="18" strokeLinecap="round" />
      <path d="M128 75 H194 L224 116 L160 168 L96 116 Z" fill={shape === "logo-gem" ? color : "none"} stroke="#263238" strokeWidth="6" />
      <circle cx="160" cy="116" r="56" fill="none" stroke={shape === "logo-ring" ? color : "#263238"} strokeWidth="16" />
      <path d="M172 53 L112 127 H158 L145 178 L210 101 H166 Z" fill={shape === "logo-bolt" ? color : "#fed766"} stroke="#263238" strokeWidth="5" />
      <rect x="98" y="72" width="124" height="92" rx="18" fill={shape === "logo-tile" ? color : "transparent"} opacity={shape === "logo-tile" ? "1" : "0"} />
      <path d="M104 82 C130 30 194 36 214 88 C178 78 142 112 126 160 C98 136 91 105 104 82 Z" fill={shape === "logo-leaf" ? color : "transparent"} />
      <path d="M160 56 L177 97 L220 100 L187 128 L197 170 L160 147 L123 170 L133 128 L100 100 L143 97 Z" fill={shape === "logo-star" ? color : "transparent"} stroke={shape === "logo-star" ? "#263238" : "transparent"} strokeWidth="5" />
      <rect x="108" y="134" width="24" height="35" rx="8" fill={shape === "logo-bars" ? color : "#dde7ef"} />
      <rect x="148" y="104" width="24" height="65" rx="8" fill={shape === "logo-bars" ? color : "#dde7ef"} />
      <rect x="188" y="76" width="24" height="93" rx="8" fill={shape === "logo-bars" ? color : "#dde7ef"} />
      <rect x="112" y="70" width="96" height="96" rx="28" fill={shape === "logo-badge" ? color : "transparent"} opacity={shape === "logo-badge" ? "1" : "0"} />
    </svg>
  );
}

function FlagArt({ shape, color }) {
  return (
    <svg className="target-art" viewBox="0 0 320 230" role="img" aria-label="Flag color challenge">
      <rect x="42" y="52" width="236" height="136" rx="10" fill="#ffffff" stroke="#263238" strokeWidth="6" />
      {shape === "flag-nordic" && <><rect x="42" y="92" width="236" height="28" fill={color} /><rect x="112" y="52" width="28" height="136" fill={color} /></>}
      {shape === "flag-sun" && <><rect x="42" y="52" width="236" height="136" fill="#74c9f2" /><circle cx="160" cy="120" r="36" fill={color} /></>}
      {shape === "flag-vertical" && <><rect x="42" y="52" width="78" height="136" fill={color} /><rect x="120" y="52" width="78" height="136" fill="#fff" /><rect x="198" y="52" width="80" height="136" fill="#e95d5d" /></>}
      {shape === "flag-diagonal" && <><rect x="42" y="52" width="236" height="136" fill="#294c77" /><path d="M39 174 L262 49 L281 78 L60 192 Z" fill={color} /></>}
      {shape === "flag-disc" && <circle cx="160" cy="120" r="42" fill={color} />}
      {shape === "flag-horizontal" && <><rect x="42" y="52" width="236" height="45" fill="#f7d35d" /><rect x="42" y="97" width="236" height="45" fill="#fff" /><rect x="42" y="142" width="236" height="46" fill={color} /></>}
      {shape === "flag-canton" && <><rect x="42" y="52" width="236" height="136" fill="#f3f0e8" /><rect x="42" y="52" width="88" height="70" fill={color} /></>}
      {shape === "flag-wave" && <><rect x="42" y="52" width="236" height="136" fill="#ffe8a3" /><path d="M42 126 C82 92 115 158 158 124 C196 94 225 151 278 113 L278 151 C222 190 198 129 160 160 C117 196 82 130 42 164 Z" fill={color} /></>}
      {shape === "flag-triangle" && <><rect x="42" y="52" width="236" height="136" fill="#f4f7fb" /><path d="M42 52 L168 120 L42 188 Z" fill={color} /></>}
      {shape === "flag-stars" && <><rect x="42" y="52" width="236" height="136" fill="#224" /><text x="160" y="132" textAnchor="middle" fontSize="60" fill={color}>★ ★</text></>}
    </svg>
  );
}

function PaletteArt({ shape, color }) {
  const sets = {
    "palette-bubble": ["#ffe66d", color, "#7bdff2", "#f1f3f5"],
    "palette-lagoon": ["#fffd82", "#b9fbc0", color, "#ffffff"],
    "palette-sunset": ["#5e60ce", color, "#ffd166", "#fff3cf"],
    "palette-mint": ["#25283d", color, "#f8f7ff", "#ff7096"],
    "palette-midnight": [color, "#ffcf56", "#5ad2f4", "#fbf7f0"]
  };
  return (
    <svg className="target-art" viewBox="0 0 320 230" role="img" aria-label="Toon tones palette">
      <rect x="24" y="20" width="272" height="190" rx="28" fill="#fff8e4" />
      {sets[shape].map((fill, index) => (
        <rect key={fill + index} x={52 + index * 56} y="66" width="48" height="98" rx="16" fill={fill} stroke="#263238" strokeWidth="5" />
      ))}
      <circle cx="96" cy="180" r="10" fill={sets[shape][0]} />
      <circle cx="142" cy="180" r="10" fill={sets[shape][1]} />
      <circle cx="188" cy="180" r="10" fill={sets[shape][2]} />
      <circle cx="234" cy="180" r="10" fill={sets[shape][3]} />
    </svg>
  );
}

function SeoContent({ path, navigate }) {
  const page = PAGE_META[path];
  return (
    <section className="seo-section">
      <div className="seo-grid">
        <article>
          <h2>Free Toon Tone Color Guessing</h2>
          <p>
            Toon Tone, toontone, toon tones, toon-tone, and tone toon all point to
            the same satisfying idea: remember a color, rebuild it with HSB sliders,
            and see how close your eye can get. This independent Toon Tone Game uses
            original cartoon art, abstract brand-style shapes, flag-inspired layouts,
            and palette practice.
          </p>
          <p>
            The game works directly in your browser, including mobile. Each game has
            five rounds, a 500 point maximum, local best scores, and a shareable result.
          </p>
        </article>
        <article>
          <h2>Pages</h2>
          <div className="page-pills">
            {Object.entries(PAGE_META).map(([href, meta]) => (
              <button key={href} type="button" onClick={() => navigate(href)} className={path === href ? "active" : ""}>
                {meta.h1}
              </button>
            ))}
          </div>
        </article>
      </div>
      <FAQSection title={page.h1} />
    </section>
  );
}

function FAQSection() {
  const faqs = [
    [
      "What is Toon Tone Game?",
      "Toon Tone Game is a free browser color guessing game where you match toon-inspired colors using hue, saturation, and brightness sliders."
    ],
    [
      "Is this the original Toon Tone?",
      "This is an independent color memory game inspired by the toon color guessing format. It uses original artwork and multiple color challenge modes."
    ],
    [
      "Can I play without signing up?",
      "Yes. The game is free and works directly in the browser."
    ],
    [
      "How is the score calculated?",
      "Each guess is compared with the target color using hue, saturation, and brightness differences. The closer your color is, the higher your score."
    ]
  ];

  return (
    <section className="faq">
      <h2>FAQ</h2>
      {faqs.map(([question, answer]) => (
        <details key={question} open>
          <summary>{question}</summary>
          <p>{answer}</p>
        </details>
      ))}
    </section>
  );
}

createRoot(document.getElementById("root")).render(<App />);
